import type { ScheduleResult } from "@planerius/contracts/cabinet"
import type { CabinetBranchAccess } from "@planerius/contracts/cabinet-auth"
import { useEffect, useMemo, useState } from "react"

import { useCabinetAccess } from "../../access/use-access"
import { fetchJournal } from "../../api/journal.api"
import type { ApiProblem } from "../../api/problem"
import { isApiProblem } from "../../api/problem"
import { shiftDate, todayInZone } from "../../lib/format"
import { resolveBranch } from "../../shell/use-branch"
import { weekDates, weekStart } from "../directories/staff/schedule-model"
import type { MasterWeekView } from "./master-week"
import { buildMasterWeek } from "./master-week"
import type { MasterSearch } from "./use-master-day"

/**
 * Неделя мастера: по одному запросу на филиал, сшивка по датам.
 *
 * ПОЧЕМУ НЕ ОДИН ЗАПРОС. У журнала `branchId` обязателен, и «все точки» ему запрещены
 * не из осторожности: у филиалов разные часовые зоны, шаг сетки и часы работы — один
 * ответ на два филиала пришлось бы считать в чьей-то чужой зоне. Мастер же работает
 * в двух точках, и макет требует подписать каждую смену её филиалом.
 *
 * ЗАПРОС ОДИН НА ФИЛИАЛ, А НЕ СЕМЬ НА ДЕНЬ. Контракт разрешает диапазон до 31 дня,
 * поэтому неделя — это ровно один запрос на точку. Семь запросов на филиал дали бы
 * четырнадцать обращений там, где хватает двух.
 *
 * ОТКАЗ ОДНОГО ФИЛИАЛА НЕ ПРЯЧЕТ ОСТАЛЬНЫЕ. Мастеру с двумя точками неделя нужна
 * целиком, но половина недели лучше, чем экран ошибки: филиал, который не ответил,
 * называется словами, а остальные дни показываются. При этом неполная неделя помечается
 * `partial`, и по ней запрещено утверждать «смен нет»: это утверждение означает
 * «вопрос к администратору», и выдать его из-за таймаута значит послать человека
 * выяснять то, чего не было.
 *
 * ФИЛИАЛЫ СПРАШИВАЮТСЯ ОДНОВРЕМЕННО. Последовательный обход умножал потолок ожидания
 * на число точек: одна зависшая точка держала всю неделю десять секунд, пять точек —
 * до пятидесяти, хотя четыре ответа уже лежали в памяти.
 *
 * НЕДЕЛЯ ЗАПРАШИВАЕТСЯ НА ДЕНЬ ШИРЕ. Ночная смена 22:00–06:00 — поддерживаемый случай,
 * и записи после полуночи лежат за верхней границей интервала: без лишнего дня
 * воскресная ночь выглядела бы свободной, будучи проданной целиком. Лишний день
 * отбрасывается при сборке — дни строятся по `dates`.
 */

export interface MasterWeekData {
  /** Понедельник показанной недели. */
  readonly start: string
  readonly today: string
  readonly timeZone: string
  readonly employeeId: string | null
  readonly branches: readonly CabinetBranchAccess[]
  /** Названия выключенных точек мастера: их смены в неделю не попали. */
  readonly disabledBranches: readonly string[]
  readonly view: MasterWeekView | null
  /** Филиалы, которые не ответили. Пусто — всё загрузилось. */
  readonly failedBranches: readonly string[]
  readonly loading: boolean
  readonly problem: ApiProblem | null
  /** Момент сборки данных: в офлайне мастер должен знать, чему он верит. */
  readonly generatedAt: string | null
  readonly reload: () => void
}

export function useMasterWeek(search: MasterSearch): MasterWeekData {
  const access = useCabinetAccess()
  const context = access.kind === "ready" ? access.context : null
  const employeeId = context?.employeeId ?? null

  const branch = context === null ? null : resolveBranch(context, search.branch ?? null)
  const timeZone = branch?.timezone ?? context?.tenant.defaultTimezone ?? "Europe/Moscow"
  const today = todayInZone(timeZone)
  const start = weekStart(search.date ?? today)
  const dates = useMemo(() => weekDates(start), [start])

  /*
   * ВСЕ ФИЛИАЛЫ МАСТЕРА, А НЕ ВЫБРАННЫЙ. «Моя неделя» отвечает на вопрос «когда
   * я работаю», а не «когда я работаю здесь»: смена во второй точке — это тоже
   * рабочий день, и не увидеть её значит прийти в свой выходной или прогулять смену.
   */
  const branches = useMemo(
    () => (context === null ? [] : context.branches.filter((item) => item.isActive)),
    [context],
  )

  /*
   * ВЫКЛЮЧЕННЫЙ ФИЛИАЛ НАЗЫВАЕТСЯ, А НЕ ИСЧЕЗАЕТ. Смены мастера в выключенной точке
   * в графике остаются, и молча выбросить точку значит показать эти дни выходными.
   * А если выключены все точки, «вам не открыт ни один филиал» — прямая неправда:
   * доступ у мастера есть, работать сейчас негде.
   */
  const disabledBranches = useMemo(
    () =>
      context === null
        ? []
        : context.branches.filter((item) => !item.isActive).map((item) => item.name),
    [context],
  )

  const [state, setState] = useState<{
    readonly key: string
    readonly results: readonly { result: ScheduleResult; branchName: string }[]
    readonly failed: readonly string[]
    readonly loading: boolean
    readonly problem: ApiProblem | null
  }>({ key: "", results: [], failed: [], loading: true, problem: null })

  const [attempt, setAttempt] = useState(0)
  /*
   * ПОПЫТКА НЕ ВХОДИТ В КЛЮЧ. Ключ отвечает на вопрос «те ли это данные», и повторная
   * загрузка той же недели данные не обесценивает: обновление по возврату на вкладку
   * не должно мигать каркасом на месте уже показанной недели.
   */
  const key = `${start}:${branches.map((item) => item.id).join(",")}`

  useEffect(() => {
    if (employeeId === null || branches.length === 0) {
      setState({ key, results: [], failed: [], loading: false, problem: null })
      return
    }
    const controller = new AbortController()
    /*
     * ПРИ СМЕНЕ НЕДЕЛИ ДАННЫЕ СТИРАЮТСЯ. Прежние ответы, сшитые с новыми датами, дают
     * семь дней без смен — и экран уверенно печатал «На этой неделе смен нет» сразу
     * после нажатия «›», хотя смены там есть и ещё грузятся. Каркас честнее.
     */
    setState((current) =>
      current.key === key
        ? { ...current, loading: true }
        : { key, results: [], failed: [], loading: true, problem: null },
    )

    void (async () => {
      const settled = await Promise.allSettled(
        branches.map((item) =>
          fetchJournal(
            {
              branchId: item.id,
              dateFrom: dates[0] ?? start,
              // На день шире конца недели — ради записей ночной смены воскресенья.
              // Контракт разрешает диапазон до 31 дня, лишний день отбросит сборка.
              dateTo: shiftDate(dates[dates.length - 1] ?? start, 1),
              employeeIds: [employeeId],
            },
            controller.signal,
          ),
        ),
      )
      if (controller.signal.aborted) return

      const results: { result: ScheduleResult; branchName: string }[] = []
      const failed: string[] = []
      let problem: ApiProblem | null = null

      settled.forEach((outcome, index) => {
        const item = branches[index]
        if (item === undefined) return
        if (outcome.status === "fulfilled") {
          results.push({ result: outcome.value, branchName: item.name })
          return
        }
        failed.push(item.name)
        // Отказ запоминается, но не прячет остальные точки: вторая могла ответить.
        if (isApiProblem(outcome.reason)) problem = outcome.reason
      })
      setState({
        key,
        results,
        failed,
        loading: false,
        // Экран ошибки — только когда не ответил НИ ОДИН филиал: иначе половина
        // недели полезнее, чем «повторите попытку».
        problem: results.length === 0 ? problem : null,
      })
    })()

    return () => {
      controller.abort()
    }
  }, [key, attempt, employeeId, branches, dates, start])

  /*
   * НЕДЕЛЯ ПЕРЕЧИТЫВАЕТСЯ ПРИ ВОЗВРАЩЕНИИ И ПРИ ПОЯВЛЕНИИ СВЯЗИ. Мастер смотрит этот
   * экран вечером и утром на том же самом открытом телефоне: без перечитывания вторник,
   * которому ночью поставили смену, остаётся «выходным», и ничто на экране не говорит,
   * что данные вечерние.
   */
  useEffect(() => {
    const refresh = (): void => {
      if (document.visibilityState !== "visible") return
      setAttempt((value) => value + 1)
    }
    document.addEventListener("visibilitychange", refresh)
    window.addEventListener("online", refresh)
    return () => {
      document.removeEventListener("visibilitychange", refresh)
      window.removeEventListener("online", refresh)
    }
  }, [])

  const view = useMemo(
    () =>
      state.results.length === 0
        ? null
        : buildMasterWeek({
            dates,
            today,
            results: state.results,
            partial: state.failed.length > 0,
          }),
    [state.results, state.failed, dates, today],
  )

  return {
    start,
    today,
    timeZone,
    employeeId,
    branches,
    disabledBranches,
    view,
    failedBranches: state.failed,
    loading: state.loading,
    problem: state.problem,
    generatedAt: state.results[0]?.result.generatedAt ?? null,
    reload: () => {
      setAttempt((value) => value + 1)
    },
  }
}
